import {
  ChangeDetectorRef,
  Component,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { DatePickerModule } from '@syncfusion/ej2-angular-calendars';
import {
  DayService,
  EventSettingsModel,
  MonthService,
  ScheduleComponent,
  ScheduleModule,
  TimelineMonthService,
  TimelineViewsService,
  WeekService,
  WorkWeekService,
} from '@syncfusion/ej2-angular-schedule';
import { loadCldr } from '@syncfusion/ej2-base';
import daGregorian from '@syncfusion/ej2-cldr-data/main/da/ca-gregorian.json';
import daNumberData from '@syncfusion/ej2-cldr-data/main/da/numbers.json';
import daTimeZoneData from '@syncfusion/ej2-cldr-data/main/da/timeZoneNames.json';
import numberingSystem from '@syncfusion/ej2-cldr-data/supplemental/numberingSystems.json';
import {
  catchError,
  filter,
  finalize,
  forkJoin,
  map,
  of,
  Subscription,
  take,
  timeout,
} from 'rxjs';
import {
  EventDTO,
  EventOrganizationResourceDTO,
} from '../../../dto/event/event.dto';
import { EventSubEventDTO } from '../../../dto/event/event-sub-event.dto';
import { Location } from '../../../models/location/Location.model';
import { Room } from '../../../models/location/Room.model';
import { AuthService } from '../../../services/auth/auth.service';
import { buildNewEventDateDefaults } from '../../../services/component-data-dest/data/add-event';
import { ComponentDataSourceService } from '../../../services/component-data-source/component-data-source.service';
import {
  normalizeEventIsActive,
  normalizeEventIsShown,
} from '../../../services/component-data-source/data/event/event-active.util';
import {
  buildEventCategoryInheritanceContext,
  EventCategoryInheritanceContext,
  EventCategoryColorsMap,
  normalizeEventCategoryColors,
  resolveEventCategoryBackgroundColor,
  resolveEventCategoryBorderColor,
} from '../../../services/component-data-source/data/event/event-category-color.util';

loadCldr(daNumberData, daTimeZoneData, daGregorian, numberingSystem);

type EventType = 'education' | 'course' | 'exercise';
type ScheduleViewName = 'TimelineDay' | 'TimelineWeek' | 'TimelineMonth';

interface OrgDefaults {
  locationGuids: string[];
  roomGuids: string[];
  orgResourceIds: number[];
  eventTypes: EventType[];
  showManualLocations: boolean;
  showLocationRows: boolean;
}

interface OrgBookableResource {
  id: number;
  label: string;
  is_active?: boolean;
}

interface ResourceItem {
  ResourceId: string;
  ResourceText: string;
  ResourceColor: string;
  IsRoom: boolean;
  Level: 'location' | 'room' | 'manual' | 'org_resource';
  LocationGuid: string | null;
  RoomGuid: string | null;
  OrgResourceId: number | null;
}

interface ScheduleEvent {
  Id: number;
  Guid: string;
  Subject: string;
  StartTime: Date;
  EndTime: Date;
  ResourceId: string;
  EventType: EventType;
  LocationName: string;
  RoomName: string;
  IsActive: boolean;
  IsShown: boolean;
  /** Normalized flags (avoid Syncfusion / legacy 0|1 confusing tooltips). */
  EventActive: boolean;
  EventShown: boolean;
  CategoryColor?: string | null;
}

/** One booked occurrence (main day or later day in a series) used for room occupancy. */
interface OccupancyOccurrence {
  eventId: number;
  guid: string;
  subject: string;
  start: Date;
  end: Date;
  eventType: EventType;
  categoryColor: string | null;
  locationGuid: string | null;
  roomGuid: string | null;
  manualLocation: string;
  locationName: string;
  roomName: string;
  additionalRoomGuids: string[];
  organizationResourceIds: number[];
  organizationResources: EventOrganizationResourceDTO[];
  isActive: boolean;
  isShown: boolean;
}

@Component({
  selector: 'app-duna-ressource-overview',
  standalone: true,
  imports: [FormsModule, ScheduleModule, DatePickerModule],
  providers: [
    DayService,
    WeekService,
    WorkWeekService,
    MonthService,
    TimelineViewsService,
    TimelineMonthService,
  ],
  templateUrl: './duna-ressource-overview.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './duna-ressource-overview.component.scss',
})
export class DunaRessourceOverviewComponent implements OnInit, OnDestroy {
  @ViewChild('scheduleObj') scheduleObj?: ScheduleComponent;

  /** Data sources default to pageSize 10; use a high cap to avoid truncating org resources. */
  private static readonly OVERVIEW_LIST_PAGE_SIZE = 10000;

  readonly eventTypeOptions: { value: EventType; label: string }[] = [
    { value: 'education', label: 'Uddannelser' },
    { value: 'course', label: 'Kurser' },
    { value: 'exercise', label: 'Øvelser' },
  ];

  canAddEventsCached = false;
  private readonly todayStart = this.startOfDay(new Date());
  private readonly addEventCellTypes = new Set(['workCells', 'monthCells']);
  private readonly currentDayCellTypes = new Set([
    'workCells',
    'monthCells',
    'dateHeader',
    'majorSlot',
    'minorSlot',
  ]);
  private readonly allowedAddEventRoles = new Set([
    'superadmin',
    'orgadmin',
    'orgbruger',
    'uddansvarlig',
  ]);

  selectedDate = this.startOfDay(new Date());
  currentView: ScheduleViewName = 'TimelineWeek';

  scheduleViews: any[] = [
    { option: 'TimelineDay', interval: 1, displayName: 'Dag' },
    { option: 'TimelineWeek', interval: 1, displayName: 'Uge' },
    { option: 'TimelineMonth', interval: 1, displayName: 'Måned' },
  ];
  scheduleGroup = { resources: ['Resources'] };
  eventSettings: EventSettingsModel = {
    dataSource: [],
    fields: {
      subject: { name: 'Subject' },
      startTime: { name: 'StartTime' },
      endTime: { name: 'EndTime' },
    },
  };
  resourceDataSource: ResourceItem[] = [];
  /** Stable reference so change detection does not recreate the schedule resource group. */
  scheduleResources: {
    field: string;
    title: string;
    name: string;
    allowMultiple: boolean;
    dataSource: ResourceItem[];
    textField: string;
    idField: string;
    colorField: string;
  }[] = [
    {
      field: 'ResourceId',
      title: 'Lokation / Lokale / Ressource',
      name: 'Resources',
      allowMultiple: false,
      dataSource: [],
      textField: 'ResourceText',
      idField: 'ResourceId',
      colorField: 'ResourceColor',
    },
  ];

  isLoading = false;
  errorMessage = '';
  showSettings = false;
  private isLoadingRange = false;
  private lastRequestedRangeKey: string | null = null;
  private cachedRangeEvents: EventDTO[] = [];
  private silentScheduleUpdateDepth = 0;
  private lastAppliedStartHour: string | null = null;
  private lastAppliedEndHour: string | null = null;
  private scheduleClickHandler?: (event: MouseEvent) => void;
  private lastAddEventAt = 0;
  /**
   * Native date pickers (Chrome/Edge on Windows) can click-through when the
   * previous-month control is used. Ignore add-event clicks until this time.
   */
  private ignoreScheduleSlotClicksUntil = 0;
  timeScale: { enable: boolean; interval: number; slotCount: number } = {
    enable: false,
    interval: 60,
    slotCount: 1,
  };

  eventTypeFilters: Record<EventType, boolean> = {
    education: true,
    course: true,
    exercise: true,
  };

  /** When true, drop location/room rows with no events in the loaded range; keep a location if it has events or any visible room under it does. */
  hideResourcesWithoutEvents = false;

  /** When true, events with manually entered locations appear as extra rows (grouped by location name). */
  showManualLocations = true;

  /** When true, locations appear as their own rows. When false, only rooms are shown if the location has rooms. */
  showLocationRows = true;

  /** Day view only: when true, timeline shows 08:00–18:00 (night hours hidden). */
  hideNightHoursInDayView = true;

  readonly dayWorkHours = { highlight: true, start: '08:00', end: '18:00' };
  private readonly fullDayHours = { start: '00:00', end: '24:00' };

  allLocations: Location[] = [];
  allRooms: Room[] = [];
  allOrgResources: OrgBookableResource[] = [];
  allEvents: ScheduleEvent[] = [];
  private eventCategoryColors: EventCategoryColorsMap = {};
  private eventCategoryColorsOrgId: number | null = null;
  private authReadySubscription?: Subscription;
  orgDefaults: OrgDefaults = this.emptyOrgDefaults();

  visibleLocationGuids = new Set<string>();
  visibleRoomGuids = new Set<string>();
  visibleOrgResourceIds = new Set<number>();

  constructor(
    private readonly dataSourceService: ComponentDataSourceService,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.canAddEventsCached = this.computeCanAddEvents();
    this.syncTimeScaleToCurrentView();
    this.loadEventCategoryColors();
    this.authReadySubscription = this.authService.authStateReady$
      .pipe(filter((ready) => ready === true))
      .subscribe(() => this.loadEventCategoryColors());
    this.loadOverviewData();
  }

  ngOnDestroy(): void {
    this.authReadySubscription?.unsubscribe();
    this.unbindScheduleClickHandler();
  }

  get currentRangeLabel(): string {
    if (this.scheduleObj?.activeView?.renderDates?.length) {
      const dates = this.scheduleObj.activeView.renderDates;
      const start = dates[0];
      const end = dates[dates.length - 1];
      return `${this.formatDate(start)} - ${this.formatDate(end)}`;
    }
    return this.formatDate(this.selectedDate);
  }

  get showNightHoursFilter(): boolean {
    return this.currentView === 'TimelineDay';
  }

  get showDayDatePicker(): boolean {
    return this.currentView === 'TimelineDay';
  }

  get scheduleStartHour(): string {
    if (this.showNightHoursFilter && this.hideNightHoursInDayView) {
      return this.dayWorkHours.start;
    }
    return this.fullDayHours.start;
  }

  get scheduleEndHour(): string {
    if (this.showNightHoursFilter && this.hideNightHoursInDayView) {
      return this.dayWorkHours.end;
    }
    return this.fullDayHours.end;
  }

  toggleSettings(): void {
    this.showSettings = !this.showSettings;
  }

  onScheduleNavigated(): void {
    this.runInZone(() => {
      if (this.silentScheduleUpdateDepth > 0) {
        return;
      }
      const changed = this.syncSelectedDateAndViewFromSchedule();
      this.syncTimeScaleToCurrentView();
      this.refreshScheduleVisibleHours();
      if (changed) {
        this.loadEventsForVisibleRange();
      }
    });
  }

  onScheduleActionComplete(args: any): void {
    this.runInZone(() => {
      if (this.silentScheduleUpdateDepth > 0) {
        return;
      }
      const changed = this.syncSelectedDateAndViewFromSchedule();
      this.syncTimeScaleToCurrentView();

      const requestType = args?.requestType;
      if (
        requestType === 'viewNavigate' ||
        requestType === 'dateNavigate' ||
        changed
      ) {
        this.refreshScheduleVisibleHours();
        this.loadEventsForVisibleRange();
      } else {
        this.expandScheduleToFitContent();
      }
      this.bindScheduleClickHandler();
    });
  }

  onScheduleDataBound(): void {
    this.runInZone(() => {
      if (this.silentScheduleUpdateDepth > 0) {
        return;
      }
      this.expandScheduleToFitContent();
      this.bindScheduleClickHandler();
    });
  }

  onRenderCell(args: any): void {
    const element = args?.element as HTMLElement | undefined;
    if (!element) {
      return;
    }

    const type = args.elementType;
    if (this.addEventCellTypes.has(type)) {
      element.classList.toggle('duna-add-event-cell', this.canAddEventsCached);
    }

    if (!args?.date) {
      return;
    }
    if (
      this.currentView !== 'TimelineWeek' &&
      this.currentView !== 'TimelineMonth'
    ) {
      return;
    }
    if (!this.isSameDay(args.date, this.todayStart)) {
      return;
    }

    if (this.currentDayCellTypes.has(type)) {
      element.classList.add('duna-current-day');
    }
  }

  trackByGuid = (_: number, item: { guid: string }): string => item.guid;
  trackByOrgResourceId = (_: number, item: { id: number }): number => item.id;

  goToPreviousPeriod(): void {
    this.selectedDate = this.addDays(
      this.selectedDate,
      -this.getNavigationStepDays()
    );
    this.applySelectedDateToSchedule();
    this.loadEventsForVisibleRange();
  }

  goToNextPeriod(): void {
    this.selectedDate = this.addDays(
      this.selectedDate,
      this.getNavigationStepDays()
    );
    this.applySelectedDateToSchedule();
    this.loadEventsForVisibleRange();
  }

  resetToToday(): void {
    this.goToDate(new Date());
  }

  onDayDatePickerChange(value: string | Date | null | undefined): void {
    if (value == null || value === '') {
      return;
    }
    const parsed =
      value instanceof Date ? new Date(value) : new Date(`${value}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) {
      return;
    }
    this.suppressScheduleSlotClicks(500);
    this.goToDate(parsed);
  }

  onDayDatePickerFocus(): void {
    this.ignoreScheduleSlotClicksUntil = Number.POSITIVE_INFINITY;
  }

  onDayDatePickerBlur(): void {
    this.suppressScheduleSlotClicks(500);
  }

  applyOrgDefaults(): void {
    const locationDefaults =
      this.orgDefaults.locationGuids.length > 0
        ? this.orgDefaults.locationGuids
        : this.allLocations.map((location) => location.guid);
    const roomDefaults =
      this.orgDefaults.roomGuids.length > 0
        ? this.orgDefaults.roomGuids
        : this.allRooms.map((room) => room.guid);

    this.visibleLocationGuids = new Set(locationDefaults);
    this.visibleRoomGuids = new Set(
      roomDefaults.filter((roomGuid) => {
        const room = this.allRooms.find((item) => item.guid === roomGuid);
        return (
          !room?.location_guid ||
          this.visibleLocationGuids.has(room.location_guid)
        );
      })
    );

    const orgTypes = new Set(this.orgDefaults.eventTypes);
    this.eventTypeFilters.education = orgTypes.has('education');
    this.eventTypeFilters.course = orgTypes.has('course');
    this.eventTypeFilters.exercise = orgTypes.has('exercise');
    this.showManualLocations = this.orgDefaults.showManualLocations;
    this.showLocationRows = this.orgDefaults.showLocationRows;

    const orgResourceDefaults =
      this.orgDefaults.orgResourceIds.length > 0
        ? this.orgDefaults.orgResourceIds
        : this.allOrgResources.map((resource) => resource.id);
    this.visibleOrgResourceIds = new Set(orgResourceDefaults);

    this.rebuildScheduleData();
  }

  onFilterChanged(): void {
    this.rebuildScheduleData();
  }

  onNightHoursFilterChanged(): void {
    this.refreshScheduleVisibleHours();
  }

  toggleLocationVisibility(locationGuid: string): void {
    if (this.visibleLocationGuids.has(locationGuid)) {
      this.visibleLocationGuids.delete(locationGuid);
      this.allRooms
        .filter((room) => room.location_guid === locationGuid)
        .forEach((room) => this.visibleRoomGuids.delete(room.guid));
    } else {
      this.visibleLocationGuids.add(locationGuid);
      this.allRooms
        .filter((room) => room.location_guid === locationGuid)
        .forEach((room) => this.visibleRoomGuids.add(room.guid));
    }
    this.rebuildScheduleData();
  }

  toggleRoomVisibility(roomGuid: string): void {
    const room = this.allRooms.find((item) => item.guid === roomGuid);
    if (room && !this.visibleLocationGuids.has(room.location_guid)) {
      return;
    }

    if (this.visibleRoomGuids.has(roomGuid)) {
      this.visibleRoomGuids.delete(roomGuid);
    } else {
      this.visibleRoomGuids.add(roomGuid);
    }
    this.rebuildScheduleData();
  }

  getLocationRooms(locationGuid: string): Room[] {
    return this.allRooms.filter((room) => room.location_guid === locationGuid);
  }

  isLocationVisible(locationGuid: string): boolean {
    return this.visibleLocationGuids.has(locationGuid);
  }

  isRoomVisible(roomGuid: string): boolean {
    return this.visibleRoomGuids.has(roomGuid);
  }

  toggleOrgResourceVisibility(resourceId: number): void {
    if (this.visibleOrgResourceIds.has(resourceId)) {
      this.visibleOrgResourceIds.delete(resourceId);
    } else {
      this.visibleOrgResourceIds.add(resourceId);
    }
    this.rebuildScheduleData();
  }

  isOrgResourceVisible(resourceId: number): boolean {
    return this.visibleOrgResourceIds.has(resourceId);
  }

  canAddEvents(): boolean {
    return this.canAddEventsCached;
  }

  private computeCanAddEvents(): boolean {
    const userRoles = this.authService.getUserRoles();
    return userRoles.some((role) =>
      this.allowedAddEventRoles.has(role.toLowerCase())
    );
  }

  onEventClick(args: any): void {
    const eventData = args?.event ?? args?.data ?? null;
    const event = Array.isArray(eventData) ? eventData[0] : eventData;
    const guid = this.resolveEventGuid(event);
    if (!guid) {
      return;
    }
    window.open(
      `${window.location.origin}/edit-event/${guid}`,
      '_blank',
      'noopener,noreferrer'
    );
  }

  onCellClick(args: any): void {
    if (!this.canAddEvents()) {
      return;
    }
    const target =
      (args?.element as HTMLElement | undefined) ??
      (args?.event?.target as HTMLElement | undefined);
    if (this.isAppointmentTarget(target)) {
      return;
    }
    if (args?.cancel !== undefined) {
      args.cancel = true;
    }
    this.handleScheduleSlotInteraction(
      target,
      this.toDate(args?.startTime),
      this.toDate(args?.endTime),
      args
    );
  }

  onPopupOpen(args: any): void {
    if (!this.canAddEvents()) {
      return;
    }
    if (args?.type !== 'Editor' && args?.type !== 'QuickInfo') {
      return;
    }
    const popupData = args?.data ?? {};
    if (popupData?.Guid || popupData?.guid) {
      args.cancel = true;
      return;
    }
    const start = this.toDate(
      popupData?.StartTime ?? popupData?.startTime ?? args?.startTime
    );
    if (!start) {
      return;
    }
    args.cancel = true;
    this.handleScheduleSlotInteraction(
      args?.element as HTMLElement | undefined,
      start,
      this.toDate(popupData?.EndTime ?? popupData?.endTime),
      args
    );
  }

  onScheduleWrapperClick(event: MouseEvent): void {
    if (!this.canAddEvents()) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (!target || this.shouldIgnoreScheduleClickTarget(target)) {
      return;
    }
    this.handleScheduleSlotInteraction(target, undefined, undefined, event);
  }

  goToAddEvent(date: Date, end?: Date, resource?: ResourceItem | null): void {
    if (!this.canAddEvents()) {
      return;
    }
    const now = Date.now();
    if (now - this.lastAddEventAt < 400) {
      return;
    }
    this.lastAddEventAt = now;

    const defaults = buildNewEventDateDefaults(date, end);
    const queryParams: Record<string, string> = {
      start_date: defaults.start_date,
      end_date: defaults.end_date,
    };
    this.applyResourceQueryParams(queryParams, resource);

    this.ngZone.run(() => {
      void this.router.navigate(['/add-event'], { queryParams });
    });
  }

  private applyResourceQueryParams(
    queryParams: Record<string, string>,
    resource?: ResourceItem | null
  ): void {
    if (!resource) {
      return;
    }
    if (resource.Level === 'location' && resource.LocationGuid) {
      queryParams['location'] = resource.LocationGuid;
      return;
    }
    if (resource.Level === 'room') {
      if (resource.LocationGuid) {
        queryParams['location'] = resource.LocationGuid;
      }
      if (resource.RoomGuid) {
        queryParams['room'] = resource.RoomGuid;
      }
      return;
    }
    if (resource.Level === 'manual') {
      queryParams['location'] = 'manual';
      const manualName = resource.ResourceText.replace(
        /^\[Manuel lokation\]\s*/,
        ''
      ).trim();
      if (manualName) {
        queryParams['manual_location'] = manualName;
      }
      return;
    }
    if (resource.Level === 'org_resource' && resource.OrgResourceId) {
      queryParams['organization_resource_ids'] = String(resource.OrgResourceId);
    }
  }

  private resolveClickedResource(
    args: any,
    target?: HTMLElement | null
  ): ResourceItem | null {
    const groupIndex =
      args?.groupIndex ?? args?.group?.groupIndex ?? args?.data?.groupIndex;
    const schedule = this.scheduleObj as ScheduleComponent & {
      getResourcesByIndex?: (index: number) => { resourceData?: ResourceItem };
      getCellDetails?: (element: Element) => { groupIndex?: number };
    };

    let index = typeof groupIndex === 'number' ? groupIndex : null;
    if (index == null && target && schedule?.getCellDetails) {
      const workCell = this.findWorkCell(target);
      if (workCell) {
        try {
          const details = schedule.getCellDetails(workCell);
          if (typeof details?.groupIndex === 'number') {
            index = details.groupIndex;
          }
        } catch {
          // Fall through to ResourceId lookup
        }
      }
    }

    if (index != null && schedule?.getResourcesByIndex) {
      try {
        const resourceInfo = schedule.getResourcesByIndex(index);
        const data = resourceInfo?.resourceData as
          | Record<string, unknown>
          | ResourceItem
          | undefined;
        const resourceId =
          typeof data?.['ResourceId'] === 'string' ? data['ResourceId'] : null;
        if (resourceId) {
          return (
            this.resourceDataSource.find(
              (item) => item.ResourceId === resourceId
            ) ?? null
          );
        }
      } catch {
        // Fall through to ResourceId lookup
      }
    }

    const resourceId = this.firstNonEmptyString([
      args?.ResourceId,
      args?.data?.ResourceId,
      args?.group?.ResourceId,
    ]);
    if (resourceId) {
      return (
        this.resourceDataSource.find(
          (item) => item.ResourceId === resourceId
        ) ?? null
      );
    }
    return null;
  }

  private isAppointmentTarget(target: HTMLElement | null | undefined): boolean {
    return (
      !!target?.closest('.e-appointment') ||
      !!target?.closest('.e-more-indicator')
    );
  }

  private shouldIgnoreScheduleClickTarget(target: HTMLElement): boolean {
    return !!(
      target.closest('.e-appointment') ||
      target.closest('.e-more-indicator') ||
      target.closest('.e-resource-cells') ||
      target.closest('.e-resource-left-td') ||
      target.closest('.e-toolbar') ||
      target.closest('.e-schedule-toolbar') ||
      this.isDatePickerOverlay(target)
    );
  }

  private isDatePickerOverlay(target: HTMLElement | null | undefined): boolean {
    return !!(
      target?.closest('.e-calendar') ||
      target?.closest('.e-datepicker') ||
      target?.closest('.e-datetimepicker') ||
      target?.closest('.e-header-popup') ||
      target?.closest('.day-date-picker')
    );
  }

  private suppressScheduleSlotClicks(durationMs: number): void {
    this.ignoreScheduleSlotClicksUntil = Date.now() + durationMs;
  }

  private isScheduleSlotClickSuppressed(): boolean {
    return Date.now() < this.ignoreScheduleSlotClicksUntil;
  }

  private handleScheduleSlotInteraction(
    target: HTMLElement | null | undefined,
    cellStart?: Date | null,
    cellEnd?: Date | null,
    nativeOrArgs?:
      | MouseEvent
      | {
          groupIndex?: number;
          ResourceId?: string;
          data?: unknown;
          preventDefault?: () => void;
          stopPropagation?: () => void;
        }
  ): void {
    if (!this.canAddEvents()) {
      return;
    }
    if (this.isScheduleSlotClickSuppressed()) {
      return;
    }
    if (this.isAppointmentTarget(target)) {
      return;
    }

    const workCell = this.resolveWorkCellFromTarget(
      target,
      nativeOrArgs &&
        typeof (nativeOrArgs as { clientX?: number }).clientX === 'number'
        ? (nativeOrArgs as { clientX: number; clientY: number })
        : undefined
    );
    let start =
      cellStart && !Number.isNaN(cellStart.getTime()) ? cellStart : null;
    let end = cellEnd && !Number.isNaN(cellEnd.getTime()) ? cellEnd : null;
    let groupIndex: number | null =
      typeof (nativeOrArgs as { groupIndex?: number })?.groupIndex === 'number'
        ? (nativeOrArgs as { groupIndex: number }).groupIndex
        : null;

    if (workCell) {
      const details = this.getCellDetailsFromElement(workCell);
      if (!start && details?.startTime) {
        start = details.startTime;
      }
      if (!end && details?.endTime) {
        end = details.endTime;
      }
      if (groupIndex == null && typeof details?.groupIndex === 'number') {
        groupIndex = details.groupIndex;
      }
    }

    if (!start) {
      return;
    }

    if (
      nativeOrArgs &&
      'preventDefault' in nativeOrArgs &&
      typeof nativeOrArgs.preventDefault === 'function'
    ) {
      nativeOrArgs.preventDefault();
      nativeOrArgs.stopPropagation?.();
    }

    this.goToAddEvent(
      start,
      end ?? undefined,
      this.resolveClickedResource(
        {
          ...(nativeOrArgs && !('preventDefault' in nativeOrArgs)
            ? nativeOrArgs
            : {}),
          groupIndex: groupIndex ?? undefined,
        },
        target
      )
    );
  }

  private resolveWorkCellFromTarget(
    target: HTMLElement | null | undefined,
    point?: { clientX?: number; clientY?: number }
  ): HTMLElement | null {
    const fromTarget = this.findWorkCell(target);
    if (fromTarget) {
      return fromTarget;
    }
    if (
      !point ||
      typeof point.clientX !== 'number' ||
      typeof point.clientY !== 'number'
    ) {
      return null;
    }
    const stack = document.elementsFromPoint(point.clientX, point.clientY);
    for (const node of stack) {
      const element = node as HTMLElement;
      if (this.isDatePickerOverlay(element)) {
        return null;
      }
      const cell = this.findWorkCell(element);
      if (cell) {
        return cell;
      }
    }
    return null;
  }

  private getCellDetailsFromElement(
    workCell: HTMLElement
  ): { startTime?: Date; endTime?: Date; groupIndex?: number } | null {
    const schedule = this.scheduleObj as ScheduleComponent & {
      getCellDetails?: (element: Element) => {
        startTime?: Date;
        endTime?: Date;
        groupIndex?: number;
      };
    };
    if (!schedule?.getCellDetails) {
      return null;
    }
    try {
      const details = schedule.getCellDetails(workCell);
      return {
        startTime: details?.startTime ? new Date(details.startTime) : undefined,
        endTime: details?.endTime ? new Date(details.endTime) : undefined,
        groupIndex: details?.groupIndex,
      };
    } catch {
      return null;
    }
  }

  private bindScheduleClickHandler(): void {
    this.unbindScheduleClickHandler();
    const element = this.scheduleObj?.element as HTMLElement | undefined;
    if (!element || !this.canAddEvents()) {
      return;
    }
    this.scheduleClickHandler = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target || this.shouldIgnoreScheduleClickTarget(target)) {
        return;
      }
      this.ngZone.run(() => {
        this.handleScheduleSlotInteraction(target, undefined, undefined, event);
      });
    };
    element.addEventListener('click', this.scheduleClickHandler, true);
  }

  private unbindScheduleClickHandler(): void {
    const element = this.scheduleObj?.element as HTMLElement | undefined;
    if (element && this.scheduleClickHandler) {
      element.removeEventListener('click', this.scheduleClickHandler, true);
    }
    this.scheduleClickHandler = undefined;
  }

  private findWorkCell(
    target: HTMLElement | null | undefined
  ): HTMLElement | null {
    let element = target ?? null;
    while (element && !element.classList.contains('e-schedule')) {
      if (element.tagName === 'TD' || element.tagName === 'TH') {
        if (
          element.classList.contains('e-work-cells') ||
          element.classList.contains('e-work-days') ||
          element.classList.contains('e-alternate-cells') ||
          element.classList.contains('e-header-cells')
        ) {
          return element;
        }
      }
      element = element.parentElement;
    }
    return null;
  }

  onEventRendered(args: any): void {
    const element = args?.element as HTMLElement | undefined;
    if (!element) {
      return;
    }

    const eventData = args?.data ?? args?.event ?? null;
    const raw = Array.isArray(eventData) ? eventData[0] : eventData;
    if (!raw) {
      return;
    }

    const event =
      this.lookupScheduleEventByRenderedPayload(
        raw as Record<string, unknown>
      ) ?? raw;

    const hoverText = this.buildEventHoverText(event);
    if (hoverText) {
      element.setAttribute('title', hoverText);
      element.setAttribute('aria-label', hoverText.replace(/\n/g, ', '));
    }

    const categoryColor =
      typeof event.CategoryColor === 'string' ? event.CategoryColor : null;
    const eventActive = normalizeEventIsActive(
      event.EventActive ?? event.IsActive ?? event.is_active
    );
    if (categoryColor && eventActive) {
      element.style.setProperty('background-color', categoryColor, 'important');
      element.style.setProperty(
        'border-color',
        resolveEventCategoryBorderColor(categoryColor),
        'important'
      );
      element.style.setProperty('color', '#ffffff', 'important');
    }
  }

  private loadEventCategoryColors(): void {
    const user = this.authService.getCurrentUser() as Record<string, unknown> | null;
    const orgId =
      user?.['organizations_id'] ??
      (user?.['user_info'] as Record<string, unknown> | undefined)?.['organizations_id'];
    const n = typeof orgId === 'number' ? orgId : parseInt(String(orgId ?? ''), 10);
    if (!Number.isFinite(n) || n <= 0) {
      return;
    }
    if (
      this.eventCategoryColorsOrgId === n &&
      Object.keys(this.eventCategoryColors).length > 0
    ) {
      return;
    }
    const obs = this.dataSourceService.executeDataSource('org_settings', {
      organizationId: n,
    });
    if (!obs) {
      return;
    }
    obs.pipe(take(1)).subscribe((res: { data?: Record<string, unknown> }) => {
      this.eventCategoryColorsOrgId = n;
      this.eventCategoryColors = normalizeEventCategoryColors(
        res?.data?.['event_category_colors']
      );
      if (this.cachedRangeEvents.length > 0) {
        this.allEvents = this.mapEvents(this.cachedRangeEvents);
        this.rebuildScheduleData();
      }
      this.cdr.markForCheck();
    });
  }

  private loadOverviewData(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.loadEventCategoryColors();

    const locationsParams = {
      filters: [
        {
          column: 'pageSize',
          value: DunaRessourceOverviewComponent.OVERVIEW_LIST_PAGE_SIZE,
          operator: 'eq',
        },
        { column: 'pageIndex', value: 0, operator: 'eq' },
      ],
    };
    const roomsParams = {
      queryParams: {
        pageSize: DunaRessourceOverviewComponent.OVERVIEW_LIST_PAGE_SIZE,
        pageIndex: 0,
      },
    };

    forkJoin({
      locations: (
        this.dataSourceService.executeDataSource(
          'locations',
          locationsParams
        ) ?? of({ items: [] })
      ).pipe(
        take(1),
        timeout(10000),
        catchError(() => of({ items: [] }))
      ),
      rooms: (
        this.dataSourceService.executeDataSource('rooms', roomsParams) ??
        of({ items: [] })
      ).pipe(
        take(1),
        timeout(10000),
        catchError(() => of({ items: [] }))
      ),
      orgResources: (
        this.dataSourceService.executeDataSource('event_org_resources', {
          queryParams: { activeOnly: 'false' },
        }) ?? of({ items: [] })
      ).pipe(
        take(1),
        timeout(10000),
        catchError(() => of({ items: [] }))
      ),
      orgDefaults: this.loadOrgDefaults().pipe(
        take(1),
        timeout(5000),
        catchError(() => of<OrgDefaults>(this.emptyOrgDefaults()))
      ),
    }).subscribe({
      next: ({ locations, rooms, orgResources, orgDefaults }) => {
        this.runInZone(() => {
          this.allLocations = ((locations?.items ?? []) as Location[]).filter(
            (location) => !!location?.guid
          );
          this.allRooms = ((rooms?.items ?? []) as Room[]).filter(
            (room) => !!room?.guid
          );
          this.allOrgResources = (
            (orgResources?.items ?? []) as OrgBookableResource[]
          ).filter((resource) => typeof resource?.id === 'number');
          this.orgDefaults = orgDefaults;
          this.applyOrgDefaults();
          this.loadEventsForVisibleRange();
        });
      },
      error: () => {
        this.runInZone(() => {
          this.errorMessage = 'Kunne ikke hente data til ressourcerne.';
          this.isLoading = false;
          this.cdr.markForCheck();
        });
      },
    });
  }

  private loadEventsForVisibleRange(): void {
    if (this.isLoadingRange) {
      return;
    }
    const range = this.getScheduleRange();
    const rangeKey = this.buildRangeKey(range);
    if (rangeKey === this.lastRequestedRangeKey) {
      return;
    }
    this.lastRequestedRangeKey = rangeKey;

    this.isLoadingRange = true;
    this.isLoading = true;
    this.errorMessage = '';

    const params = {
      queryParams: {
        startDate: range.start.toISOString(),
        endDate: range.end.toISOString(),
      },
    };

    (this.dataSourceService.executeDataSource('events', params) ?? of([]))
      .pipe(
        take(1),
        timeout(12000),
        map((events) => (Array.isArray(events) ? (events as EventDTO[]) : [])),
        catchError(() => {
          this.runInZone(() => {
            this.errorMessage =
              'Kunne ikke hente events for den valgte periode.';
            this.cdr.markForCheck();
          });
          return of([] as EventDTO[]);
        }),
        finalize(() => {
          this.runInZone(() => {
            this.isLoading = false;
            this.isLoadingRange = false;
            this.cdr.markForCheck();
          });
        })
      )
      .subscribe({
        next: (events) => {
          try {
            this.runInZone(() => {
              this.cachedRangeEvents = events;
              this.allEvents = this.mapEvents(events);
              this.rebuildScheduleData();
              this.cdr.markForCheck();
            });
          } catch (error) {
            console.error(
              '[DunaRessourceOverview] Failed while rendering events:',
              error
            );
            this.runInZone(() => {
              this.errorMessage =
                'Der opstod en fejl ved visning af ressourcerne.';
              this.cdr.markForCheck();
            });
          }
        },
      });
  }

  private rebuildScheduleData(): void {
    const allVisibleResources = this.buildVisibleResources();
    const allowedResourceIds = new Set(
      allVisibleResources.map((item) => item.ResourceId)
    );

    const filteredEvents = this.allEvents.filter((event) => {
      if (!allowedResourceIds.has(event.ResourceId)) {
        return false;
      }
      if (!this.eventTypeFilters[event.EventType]) {
        return false;
      }
      return true;
    });

    // Keep all selected resources visible, including empty ones, so the overview can show free capacity.
    this.resourceDataSource = allVisibleResources;
    this.scheduleResources[0].dataSource = allVisibleResources;

    this.eventSettings = {
      ...this.eventSettings,
      dataSource: filteredEvents,
    };

    if (this.scheduleObj) {
      this.withSilentScheduleUpdate(() => {
        this.scheduleObj?.dataBind();
        this.expandScheduleToFitContent();
      });
    }
  }

  private buildVisibleResources(): ResourceItem[] {
    if (!this.hideResourcesWithoutEvents) {
      return this.buildUserVisibleResourceTree();
    }

    const resourceIdsWithTypedEvents = this.getResourceIdsHavingTypedEvents();

    const resources: ResourceItem[] = [];
    this.allLocations
      .sort((a, b) => a.locations_name.localeCompare(b.locations_name, 'da-DK'))
      .forEach((location) => {
        if (!this.visibleLocationGuids.has(location.guid)) {
          return;
        }

        const locationResourceId = `location:${location.guid}`;
        const locationHasEvent =
          resourceIdsWithTypedEvents.has(locationResourceId);

        const visibleRoomsForLocation = this.allRooms
          .filter(
            (room) =>
              room.location_guid === location.guid &&
              this.visibleRoomGuids.has(room.guid)
          )
          .sort((a, b) => a.room_name.localeCompare(b.room_name, 'da-DK'));

        const roomsWithEvents = visibleRoomsForLocation.filter((room) =>
          resourceIdsWithTypedEvents.has(`room:${room.guid}`)
        );
        const anyVisibleRoomHasEvent = roomsWithEvents.length > 0;

        if (!locationHasEvent && !anyVisibleRoomHasEvent) {
          return;
        }

        const showLocationRow = this.shouldShowLocationRow(
          roomsWithEvents.length
        );
        if (showLocationRow) {
          resources.push(this.toLocationResource(location));
        }

        roomsWithEvents.forEach((room) => {
          resources.push(this.toRoomResource(location, room, showLocationRow));
        });
      });

    return [
      ...resources,
      ...this.buildManualLocationResources(true),
      ...this.buildOrgResourceRows(true),
    ];
  }

  /** Resources the user chose in settings (before “hide empty” pruning). */
  private buildUserVisibleResourceTree(): ResourceItem[] {
    const resources: ResourceItem[] = [];
    this.allLocations
      .sort((a, b) => a.locations_name.localeCompare(b.locations_name, 'da-DK'))
      .forEach((location) => {
        if (!this.visibleLocationGuids.has(location.guid)) {
          return;
        }

        const visibleRooms = this.allRooms
          .filter(
            (room) =>
              room.location_guid === location.guid &&
              this.visibleRoomGuids.has(room.guid)
          )
          .sort((a, b) => a.room_name.localeCompare(b.room_name, 'da-DK'));

        const showLocationRow = this.shouldShowLocationRow(visibleRooms.length);
        if (showLocationRow) {
          resources.push(this.toLocationResource(location));
        }

        visibleRooms.forEach((room) => {
          resources.push(this.toRoomResource(location, room, showLocationRow));
        });
      });

    return [
      ...resources,
      ...this.buildManualLocationResources(false),
      ...this.buildOrgResourceRows(false),
    ];
  }

  private buildOrgResourceRows(
    requireTypedEventMatch: boolean
  ): ResourceItem[] {
    const resourceIdsWithEvents = requireTypedEventMatch
      ? this.getResourceIdsHavingTypedEvents()
      : null;
    return this.allOrgResources
      .filter((resource) => this.visibleOrgResourceIds.has(resource.id))
      .filter((resource) => {
        if (!requireTypedEventMatch) {
          return true;
        }
        return (
          resourceIdsWithEvents?.has(`org_resource:${resource.id}`) ?? false
        );
      })
      .sort((a, b) => a.label.localeCompare(b.label, 'da-DK'))
      .map((resource) => ({
        ResourceId: `org_resource:${resource.id}`,
        ResourceText: `[Ressource] ${resource.label}`,
        ResourceColor: '#6b4c9a',
        IsRoom: false,
        Level: 'org_resource' as const,
        LocationGuid: null,
        RoomGuid: null,
        OrgResourceId: resource.id,
      }));
  }

  /** Manual location rows derived from events in the loaded period (same name → one row). */
  private buildManualLocationResources(
    requireTypedEventMatch: boolean
  ): ResourceItem[] {
    if (!this.showManualLocations) {
      return [];
    }

    const displayNames = new Map<string, string>();
    for (const event of this.allEvents) {
      if (!event.ResourceId.startsWith('manual:')) {
        continue;
      }
      if (requireTypedEventMatch && !this.eventTypeFilters[event.EventType]) {
        continue;
      }
      const key = event.ResourceId.slice('manual:'.length);
      if (!displayNames.has(key)) {
        displayNames.set(key, event.LocationName || key);
      }
    }

    return Array.from(displayNames.entries())
      .sort((a, b) => a[1].localeCompare(b[1], 'da-DK'))
      .map(([key, displayName]) => ({
        ResourceId: `manual:${key}`,
        ResourceText: `[Manuel lokation] ${displayName}`,
        ResourceColor: '#8f6b3e',
        IsRoom: false,
        Level: 'manual' as const,
        LocationGuid: null,
        RoomGuid: null,
        OrgResourceId: null,
      }));
  }

  private getResourceIdsHavingTypedEvents(): Set<string> {
    const ids = new Set<string>();
    for (const event of this.allEvents) {
      if (!this.eventTypeFilters[event.EventType]) {
        continue;
      }
      if (event.ResourceId) {
        ids.add(event.ResourceId);
      }
    }
    return ids;
  }

  private mapEvents(events: EventDTO[]): ScheduleEvent[] {
    const result: ScheduleEvent[] = [];
    const seenKeys = new Set<string>();
    const processedLinkedGroups = new Set<string>();
    let nextId = 1;
    const categoryContext = buildEventCategoryInheritanceContext(events);

    for (const event of events) {
      const groupId =
        typeof event.linked_event_group_id === 'string'
          ? event.linked_event_group_id.trim()
          : '';
      if (groupId) {
        if (processedLinkedGroups.has(groupId)) {
          continue;
        }
        processedLinkedGroups.add(groupId);
      }

      const sourceEvent =
        groupId
          ? this.pickSeriesAnchorEvent(events, groupId) ?? event
          : event;

      for (const occurrence of this.toOccupancyOccurrences(
        sourceEvent,
        categoryContext
      )) {
        for (const scheduleEvent of this.expandOccurrenceToScheduleEvents(
          occurrence,
          nextId
        )) {
          if (!scheduleEvent.ResourceId) {
            continue;
          }
          if (
            Number.isNaN(scheduleEvent.StartTime.getTime()) ||
            Number.isNaN(scheduleEvent.EndTime.getTime())
          ) {
            continue;
          }
          const key = [
            scheduleEvent.Guid,
            scheduleEvent.ResourceId,
            scheduleEvent.StartTime.getTime(),
            scheduleEvent.EndTime.getTime(),
          ].join('|');
          if (seenKeys.has(key)) {
            continue;
          }
          seenKeys.add(key);
          scheduleEvent.Id = nextId++;
          result.push(scheduleEvent);
        }
      }
    }

    return result;
  }

  /** One row per linked series — avoids duplicate occupancy and wrong parent is_active. */
  private pickSeriesAnchorEvent(
    events: EventDTO[],
    groupId: string
  ): EventDTO | null {
    const inGroup = events.filter(
      (row) => (row.linked_event_group_id || '').trim() === groupId
    );
    if (inGroup.length === 0) {
      return null;
    }
    const primary = inGroup.find((row) => row.series_primary === true);
    if (primary) {
      return primary;
    }
    const active = inGroup.find((row) => normalizeEventIsActive(row.is_active));
    if (active) {
      return active;
    }
    return [...inGroup].sort((a, b) =>
      String(a.start_date || '').localeCompare(String(b.start_date || ''))
    )[0];
  }

  private resolveCachedEventByGuid(guid: string): EventDTO | undefined {
    const id = guid.trim();
    if (!id) {
      return undefined;
    }
    return this.cachedRangeEvents.find((row) => (row.guid || '').trim() === id);
  }

  private lookupScheduleEventByRenderedPayload(
    raw: Record<string, unknown>
  ): ScheduleEvent | undefined {
    const numericId = raw['Id'];
    if (typeof numericId === 'number' && Number.isFinite(numericId)) {
      const byId = this.allEvents.find((item) => item.Id === numericId);
      if (byId) {
        return byId;
      }
    }
    const guid =
      typeof raw['Guid'] === 'string' ? raw['Guid'].trim() : '';
    const resourceId =
      typeof raw['ResourceId'] === 'string' ? raw['ResourceId'] : '';
    const start = this.toDate(raw['StartTime']);
    if (!guid) {
      return undefined;
    }
    const matches = this.allEvents.filter((item) => item.Guid === guid);
    if (resourceId && start) {
      const exact = matches.find(
        (item) =>
          item.ResourceId === resourceId &&
          item.StartTime.getTime() === start.getTime()
      );
      if (exact) {
        return exact;
      }
    }
    return matches[0];
  }

  /**
   * Series occupancy: later days are stored as underbegivenheder (parent_event_id children
   * or linked_event_group siblings). Nested sub_events on the main/first event carry those
   * days' rooms even when the calendar hides them.
   */
  private toOccupancyOccurrences(
    event: EventDTO,
    categoryContext: EventCategoryInheritanceContext
  ): OccupancyOccurrence[] {
    const nested = Array.isArray(event.sub_events) ? event.sub_events : [];
    const nestedOccupancy = nested
      .map((subEvent) =>
        this.subEventToOccupancy(event, subEvent, categoryContext)
      )
      .filter(
        (occurrence): occurrence is OccupancyOccurrence => occurrence != null
      );

    if (event.linked_event_group_id && nestedOccupancy.length > 0) {
      return nestedOccupancy;
    }

    if (!event.parent_event_id && nestedOccupancy.length > 0) {
      return [
        this.eventToOccupancy(event, categoryContext),
        ...nestedOccupancy,
      ];
    }

    return [this.eventToOccupancy(event, categoryContext)];
  }

  private eventToOccupancy(
    event: EventDTO,
    categoryContext: EventCategoryInheritanceContext
  ): OccupancyOccurrence {
    const manualLocation =
      typeof event.manual_location === 'string'
        ? event.manual_location.trim()
        : '';
    const start = new Date(event.start_date);
    const end = new Date(event.end_date);
    return {
      eventId: event.event_id,
      guid: typeof event.guid === 'string' ? event.guid.trim() : '',
      subject: this.resolveEventSubject(event),
      start,
      end: end > start ? end : this.addDays(start, 1),
      eventType: event.event_type as EventType,
      categoryColor: resolveEventCategoryBackgroundColor(
        event,
        this.eventCategoryColors,
        categoryContext
      ),
      locationGuid:
        event.predefined_location?.guid ??
        event.predefined_location_guid ??
        null,
      roomGuid:
        event.predefined_room?.guid ?? event.predefined_room_guid ?? null,
      manualLocation,
      locationName: event.predefined_location?.locations_name ?? manualLocation,
      roomName: event.predefined_room?.room_name ?? '',
      additionalRoomGuids: this.collectRoomGuids(event.additional_rooms),
      organizationResourceIds: this.resolveEventOrganizationResourceIds(event),
      organizationResources: event.organization_resources ?? [],
      isActive: normalizeEventIsActive(event.is_active),
      isShown: normalizeEventIsShown(event.is_shown),
    };
  }

  private subEventToOccupancy(
    parent: EventDTO,
    subEvent: EventSubEventDTO,
    categoryContext: EventCategoryInheritanceContext
  ): OccupancyOccurrence | null {
    const start = this.combineSubEventDateTime(
      subEvent.date,
      subEvent.start_time
    );
    const end = this.combineSubEventDateTime(subEvent.date, subEvent.end_time);
    if (!start || !end) {
      return null;
    }

    const manualLocation =
      typeof subEvent.manual_location === 'string'
        ? subEvent.manual_location.trim()
        : '';
    const guid =
      typeof subEvent.guid === 'string' && subEvent.guid.trim().length > 0
        ? subEvent.guid.trim()
        : typeof parent.guid === 'string'
        ? parent.guid.trim()
        : '';
    const eventId =
      subEvent.sub_event_id || subEvent.event_id || parent.event_id;
    const locationGuid =
      subEvent.predefined_location?.guid ??
      subEvent.predefined_location_guid ??
      null;
    const roomGuid =
      subEvent.predefined_room?.guid ?? subEvent.predefined_room_guid ?? null;

    const sessionEvent = this.resolveCachedEventByGuid(guid);
    const statusSource = sessionEvent ?? parent;
    const categorySource = sessionEvent ?? parent;

    return {
      eventId,
      guid,
      subject: this.resolveEventSubject(parent),
      start,
      end: end > start ? end : this.addDays(start, 1),
      eventType: parent.event_type as EventType,
      categoryColor: resolveEventCategoryBackgroundColor(
        categorySource,
        this.eventCategoryColors,
        categoryContext
      ),
      locationGuid,
      roomGuid,
      manualLocation,
      locationName:
        subEvent.predefined_location?.locations_name ??
        this.allLocations.find((location) => location.guid === locationGuid)
          ?.locations_name ??
        manualLocation,
      roomName:
        subEvent.predefined_room?.room_name ??
        this.allRooms.find((room) => room.guid === roomGuid)?.room_name ??
        '',
      additionalRoomGuids: this.collectRoomGuids(subEvent.additional_rooms),
      organizationResourceIds: this.resolveEventOrganizationResourceIds(parent),
      organizationResources: parent.organization_resources ?? [],
      isActive: normalizeEventIsActive(statusSource.is_active),
      isShown: normalizeEventIsShown(statusSource.is_shown),
    };
  }

  private expandOccurrenceToScheduleEvents(
    occurrence: OccupancyOccurrence,
    idBase: number
  ): ScheduleEvent[] {
    const base = {
      Guid: occurrence.guid,
      Subject: occurrence.subject,
      StartTime: occurrence.start,
      EndTime: occurrence.end,
      EventType: occurrence.eventType,
      LocationName: occurrence.locationName,
      RoomName: occurrence.roomName,
      IsActive: occurrence.isActive,
      IsShown: occurrence.isShown,
      EventActive: occurrence.isActive,
      EventShown: occurrence.isShown,
      CategoryColor: occurrence.categoryColor,
    };

    const resourceIds: string[] = [];
    const pushResourceId = (resourceId: string): void => {
      if (resourceId && !resourceIds.includes(resourceId)) {
        resourceIds.push(resourceId);
      }
    };

    if (occurrence.roomGuid) {
      pushResourceId(`room:${occurrence.roomGuid}`);
      if (occurrence.locationGuid) {
        pushResourceId(`location:${occurrence.locationGuid}`);
      }
    } else if (occurrence.locationGuid) {
      pushResourceId(`location:${occurrence.locationGuid}`);
    } else if (occurrence.manualLocation) {
      pushResourceId(
        this.buildManualLocationResourceId(occurrence.manualLocation)
      );
    }

    for (const extraGuid of occurrence.additionalRoomGuids) {
      pushResourceId(`room:${extraGuid}`);
    }

    const scheduleEvents: ScheduleEvent[] = resourceIds.map(
      (resourceId, index) => ({
        ...base,
        Id: idBase + index,
        ResourceId: resourceId,
      })
    );

    let orgIndex = 0;
    for (const resourceId of occurrence.organizationResourceIds) {
      const orgResource = occurrence.organizationResources.find(
        (item) => item.id === resourceId
      );
      const orgResourceLabel =
        orgResource?.label ??
        this.allOrgResources.find((item) => item.id === resourceId)?.label ??
        '';
      scheduleEvents.push({
        ...base,
        Id: idBase + 50 + orgIndex,
        ResourceId: `org_resource:${resourceId}`,
        RoomName: orgResourceLabel || occurrence.roomName,
      });
      orgIndex++;
    }

    return scheduleEvents;
  }

  private resolveEventSubject(event: EventDTO): string {
    return (
      event.description?.short_description?.['da']?.translation_text ??
      event.description?.short_description?.['en']?.translation_text ??
      'Begivenhed'
    );
  }

  private collectRoomGuids(
    rooms: Array<{ guid?: string }> | undefined
  ): string[] {
    const guids: string[] = [];
    for (const room of rooms ?? []) {
      const guid = typeof room?.guid === 'string' ? room.guid.trim() : '';
      if (guid && !guids.includes(guid)) {
        guids.push(guid);
      }
    }
    return guids;
  }

  private combineSubEventDateTime(
    dateValue: string | undefined,
    timeValue: string | undefined
  ): Date | null {
    if (typeof dateValue !== 'string' || dateValue.trim().length === 0) {
      return null;
    }

    const trimmedDate = dateValue.trim();
    if (trimmedDate.includes('T') && !timeValue) {
      const parsed = new Date(trimmedDate);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    const datePart = trimmedDate.split('T')[0];
    const timePart = (
      typeof timeValue === 'string' && timeValue.trim().length > 0
        ? timeValue.trim()
        : trimmedDate.includes('T')
        ? trimmedDate.split('T')[1]
        : '00:00:00'
    ).substring(0, 8);
    const normalizedTime = timePart.length === 5 ? `${timePart}:00` : timePart;
    const parsed = new Date(`${datePart}T${normalizedTime}`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private resolveEventGuid(event: any): string | null {
    const eventIdCandidate = event?.Id ?? event?.id ?? event?.event_id;
    const eventId =
      typeof eventIdCandidate === 'number'
        ? eventIdCandidate
        : parseInt(String(eventIdCandidate ?? ''), 10);
    if (Number.isFinite(eventId)) {
      const mappedById = this.allEvents.find((item) => item.Id === eventId);
      if (mappedById?.Guid) {
        return mappedById.Guid;
      }
    }

    const start = this.toDate(event?.StartTime ?? event?.start_date);
    const end = this.toDate(event?.EndTime ?? event?.end_date);
    const subject = this.firstNonEmptyString([event?.Subject, event?.subject]);
    const resourceId = this.firstNonEmptyString([
      event?.ResourceId,
      event?.resourceId,
    ]);

    const mappedByFingerprint = this.allEvents.find(
      (item) =>
        (!subject || item.Subject === subject) &&
        (!resourceId || item.ResourceId === resourceId) &&
        (!start || item.StartTime.getTime() === start.getTime()) &&
        (!end || item.EndTime.getTime() === end.getTime())
    );
    if (mappedByFingerprint?.Guid) {
      return mappedByFingerprint.Guid;
    }

    const candidateGuid = this.firstNonEmptyString([event?.Guid, event?.guid]);
    if (
      candidateGuid &&
      this.allEvents.some((item) => item.Guid === candidateGuid)
    ) {
      return candidateGuid;
    }

    return null;
  }

  private buildEventHoverText(event: any): string {
    const title =
      this.firstNonEmptyString([event?.Subject, event?.subject]) ??
      'Begivenhed';
    const eventType = this.firstNonEmptyString([
      event?.EventType,
      event?.event_type,
    ]) as EventType | null;
    const start = this.toDate(event?.StartTime ?? event?.start_date);
    const end = this.toDate(event?.EndTime ?? event?.end_date);
    const locationName =
      this.firstNonEmptyString([event?.LocationName, event?.location_name]) ??
      '';
    const roomName =
      this.firstNonEmptyString([event?.RoomName, event?.room_name]) ?? '';
    const lines: string[] = [title];
    if (eventType) {
      lines.push(`Type: ${this.getEventTypeLabel(eventType)}`);
    }
    if (start && end) {
      lines.push(
        `Tid: ${this.formatDateTime(start)} - ${this.formatDateTime(end)}`
      );
    }
    const locationText = [locationName, roomName]
      .filter((item) => !!item)
      .join(' - ');
    if (locationText) {
      lines.push(`Sted: ${locationText}`);
    }
    const eventActive = normalizeEventIsActive(
      event?.EventActive ?? event?.IsActive ?? event?.is_active
    );
    const eventShown = normalizeEventIsShown(
      event?.EventShown ?? event?.IsShown ?? event?.is_shown
    );
    lines.push(
      `Status: ${eventActive ? 'Aktiv' : 'Inaktiv'}, ${
        eventShown ? 'Vist' : 'Skjult'
      }`
    );
    return lines.join('\n');
  }

  private firstNonEmptyString(values: unknown[]): string | null {
    for (const value of values) {
      if (typeof value === 'string' && value.trim().length > 0) {
        return value.trim();
      }
    }
    return null;
  }

  private toDate(value: unknown): Date | null {
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }
    if (typeof value === 'string' && value.trim().length > 0) {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
  }

  private getEventTypeLabel(type: EventType): string {
    if (type === 'education') return 'Uddannelse';
    if (type === 'course') return 'Kursus';
    return 'Øvelse';
  }

  private formatDateTime(date: Date): string {
    return date.toLocaleString('da-DK', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  private loadOrgDefaults() {
    const orgId = this.resolveOrganizationId();
    if (orgId == null) {
      return of<OrgDefaults>(this.emptyOrgDefaults());
    }

    const settings$ = this.dataSourceService.executeDataSource('org_settings', {
      organizationId: orgId,
    });
    if (!settings$) {
      return of<OrgDefaults>(this.emptyOrgDefaults());
    }

    return settings$.pipe(
      map((response: { data?: Record<string, unknown> }) => {
        const data = response?.data ?? {};
        const eventTypes = this.normalizeEventTypeArray(
          data['resource_overview_default_event_types']
        );
        return {
          locationGuids: this.normalizeStringArray(
            data['resource_overview_default_location_guids']
          ),
          roomGuids: this.normalizeStringArray(
            data['resource_overview_default_room_guids']
          ),
          orgResourceIds: this.normalizeNumberArray(
            data['resource_overview_default_org_resource_ids']
          ),
          eventTypes:
            eventTypes.length > 0
              ? eventTypes
              : ['education', 'course', 'exercise'],
          showManualLocations: this.resolveShowManualLocationsDefault(
            data['resource_overview_show_manual_locations']
          ),
          showLocationRows: this.resolveShowLocationRowsDefault(
            data['resource_overview_show_location_rows']
          ),
        } as OrgDefaults;
      }),
      catchError(() => of<OrgDefaults>(this.emptyOrgDefaults()))
    );
  }

  private resolveEventOrganizationResourceIds(event: EventDTO): number[] {
    const ids = new Set<number>();
    for (const resource of event.organization_resources ?? []) {
      if (typeof resource?.id === 'number' && resource.id > 0) {
        ids.add(resource.id);
      }
    }
    const rawIds = (event as EventDTO & { organization_resource_ids?: unknown })
      .organization_resource_ids;
    if (Array.isArray(rawIds)) {
      for (const rawId of rawIds) {
        const parsed =
          typeof rawId === 'number' ? rawId : parseInt(String(rawId ?? ''), 10);
        if (Number.isFinite(parsed) && parsed > 0) {
          ids.add(parsed);
        }
      }
    }
    return Array.from(ids);
  }

  private buildManualLocationResourceId(name: string): string {
    return `manual:${this.normalizeManualLocationKey(name)}`;
  }

  private normalizeManualLocationKey(name: string): string {
    return name.trim().toLocaleLowerCase('da-DK');
  }

  /** Default is to show manual locations; org can persist false to hide them by default. */
  private resolveShowManualLocationsDefault(value: unknown): boolean {
    if (value === false || value === 'false' || value === 0) {
      return false;
    }
    return true;
  }

  /** Default is to show location rows; org can persist false to show only rooms when rooms exist. */
  private resolveShowLocationRowsDefault(value: unknown): boolean {
    if (value === false || value === 'false' || value === 0) {
      return false;
    }
    return true;
  }

  private emptyOrgDefaults(): OrgDefaults {
    return {
      locationGuids: [],
      roomGuids: [],
      orgResourceIds: [],
      eventTypes: ['education', 'course', 'exercise'],
      showManualLocations: true,
      showLocationRows: true,
    };
  }

  /** Hide the location row only when it has rooms to show instead. */
  private shouldShowLocationRow(visibleRoomCount: number): boolean {
    return this.showLocationRows || visibleRoomCount === 0;
  }

  private toLocationResource(location: Location): ResourceItem {
    return {
      ResourceId: `location:${location.guid}`,
      ResourceText: `[Lokation] ${location.locations_name}`,
      ResourceColor: '#355a93',
      IsRoom: false,
      Level: 'location',
      LocationGuid: location.guid,
      RoomGuid: null,
      OrgResourceId: null,
    };
  }

  private toRoomResource(
    location: Location,
    room: Room,
    nestedUnderLocation: boolean
  ): ResourceItem {
    return {
      ResourceId: `room:${room.guid}`,
      ResourceText: nestedUnderLocation
        ? `   - [Lokale] ${room.room_name}`
        : `[Lokale] ${room.room_name}`,
      ResourceColor: '#5f84bf',
      IsRoom: true,
      Level: 'room',
      LocationGuid: location.guid,
      RoomGuid: room.guid,
      OrgResourceId: null,
    };
  }

  private resolveOrganizationId(): number | null {
    const currentUser = this.authService.getCurrentUser();
    const orgCandidate =
      currentUser?.user_info?.organizations_id ??
      currentUser?.organizations_id ??
      null;
    if (orgCandidate == null) {
      return null;
    }

    const parsed =
      typeof orgCandidate === 'number'
        ? orgCandidate
        : parseInt(String(orgCandidate), 10);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private getScheduleRange(): { start: Date; end: Date } {
    const selected = this.startOfDay(this.selectedDate);
    if (this.currentView === 'TimelineDay') {
      return { start: selected, end: this.addDays(selected, 1) };
    }
    if (this.currentView === 'TimelineMonth') {
      const monthStart = new Date(selected.getFullYear(), selected.getMonth(), 1);
      const nextMonth = new Date(selected.getFullYear(), selected.getMonth() + 1, 1);
      return {
        start: this.startOfWeek(monthStart),
        end: this.addDays(this.startOfWeek(nextMonth), 7),
      };
    }
    const weekStart = this.startOfWeek(selected);
    return { start: weekStart, end: this.addDays(weekStart, 7) };
  }

  private buildRangeKey(range: { start: Date; end: Date }): string {
    return `${
      this.currentView
    }|${range.start.toISOString()}|${range.end.toISOString()}`;
  }

  private getNavigationStepDays(): number {
    if (this.scheduleObj?.activeView?.renderDates?.length) {
      return Math.max(1, this.scheduleObj.activeView.renderDates.length);
    }
    if (this.currentView === 'TimelineDay') return 1;
    if (this.currentView === 'TimelineMonth') return 30;
    return 7;
  }

  private syncTimeScaleToCurrentView(): void {
    const enable =
      this.currentView !== 'TimelineWeek' &&
      this.currentView !== 'TimelineMonth';
    if (
      this.timeScale.enable === enable &&
      this.timeScale.interval === 60 &&
      this.timeScale.slotCount === 1
    ) {
      return;
    }
    this.timeScale = { enable, interval: 60, slotCount: 1 };
  }

  private refreshScheduleVisibleHours(): void {
    if (!this.scheduleObj) {
      return;
    }
    const startHour = this.scheduleStartHour;
    const endHour = this.scheduleEndHour;
    if (
      this.lastAppliedStartHour === startHour &&
      this.lastAppliedEndHour === endHour &&
      this.scheduleObj.startHour === startHour &&
      this.scheduleObj.endHour === endHour
    ) {
      return;
    }
    this.lastAppliedStartHour = startHour;
    this.lastAppliedEndHour = endHour;
    this.withSilentScheduleUpdate(() => {
      if (!this.scheduleObj) {
        return;
      }
      this.scheduleObj.startHour = startHour;
      this.scheduleObj.endHour = endHour;
      this.scheduleObj.dataBind();
      this.expandScheduleToFitContent();
    });
  }

  private normalizeStringArray(value: unknown): string[] {
    if (Array.isArray(value)) {
      return value
        .map((item) => String(item).trim())
        .filter((item) => item.length > 0);
    }
    if (typeof value === 'string') {
      return value
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
    }
    return [];
  }

  private normalizeNumberArray(value: unknown): number[] {
    if (!Array.isArray(value)) {
      return [];
    }
    return value
      .map((item) => Number(item))
      .filter((item) => Number.isFinite(item) && item > 0);
  }

  private normalizeEventTypeArray(value: unknown): EventType[] {
    const allowed: EventType[] = ['education', 'course', 'exercise'];
    return this.normalizeStringArray(value).filter((item): item is EventType =>
      allowed.includes(item as EventType)
    );
  }

  private formatDate(date: Date): string {
    return date.toLocaleDateString('da-DK', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  private formatDateInputValue(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private goToDate(date: Date): void {
    this.selectedDate = this.startOfDay(date);
    this.applySelectedDateToSchedule();
    this.loadEventsForVisibleRange();
  }

  private applySelectedDateToSchedule(): void {
    if (!this.scheduleObj) {
      return;
    }
    this.withSilentScheduleUpdate(() => {
      if (!this.scheduleObj) {
        return;
      }
      this.scheduleObj.selectedDate = this.selectedDate;
      this.scheduleObj.dataBind();
    });
  }

  private syncSelectedDateAndViewFromSchedule(): boolean {
    if (!this.scheduleObj) {
      return false;
    }

    let changed = false;
    const nextDate = this.startOfDay(new Date(this.scheduleObj.selectedDate));
    if (!this.isSameDay(this.selectedDate, nextDate)) {
      this.selectedDate = nextDate;
      changed = true;
    }

    const nextView = this.scheduleObj.currentView as ScheduleViewName;
    if (nextView && nextView !== this.currentView) {
      this.currentView = nextView;
      changed = true;
    }

    return changed;
  }

  private startOfWeek(date: Date): Date {
    const start = this.startOfDay(date);
    const day = start.getDay();
    const mondayOffset = day === 0 ? -6 : 1 - day;
    return this.addDays(start, mondayOffset);
  }

  private withSilentScheduleUpdate(action: () => void): void {
    this.silentScheduleUpdateDepth++;
    try {
      action();
    } finally {
      queueMicrotask(() => {
        this.silentScheduleUpdateDepth = Math.max(
          0,
          this.silentScheduleUpdateDepth - 1
        );
      });
    }
  }

  private startOfDay(date: Date): Date {
    const result = new Date(date);
    result.setHours(0, 0, 0, 0);
    return result;
  }

  private addDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  private isSameDay(a: Date, b: Date): boolean {
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }

  private expandScheduleToFitContent(): void {
    this.ngZone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        const scheduleElement = this.scheduleObj?.element as
          | HTMLElement
          | undefined;
        if (!scheduleElement) {
          return;
        }
        this.applyNoScrollLayout(scheduleElement);
      });
    });
  }

  private applyNoScrollLayout(root: HTMLElement): void {
    const verticalOnlySelectors = [
      '.e-resource-column-wrap',
      '.e-content-wrap',
    ];

    for (const selector of verticalOnlySelectors) {
      root.querySelectorAll(selector).forEach((node) => {
        const element = node as HTMLElement;
        element.style.setProperty('overflow-y', 'visible', 'important');
        element.style.setProperty('max-height', 'none', 'important');
      });
    }

    root.style.setProperty('height', 'auto', 'important');
  }

  private runInZone(action: () => void): void {
    if (NgZone.isInAngularZone()) {
      action();
      return;
    }
    this.ngZone.run(action);
  }
}
